import {
  ALLOWED_IMAGE_CONTENT_TYPES,
  type AllowedImageContentType,
  type CatalogUploadPurpose,
  MAX_IMAGE_UPLOAD_BYTES,
  type PresignUploadResult,
} from '@repo/api-contracts';
import { useState } from 'react';
import { trpc } from '@/shared/api';

/** 브라우저·tRPC 가 준 원인 문구를 그대로 살린다 — 진단의 유일한 단서다. */
const describeCause = (cause: unknown): string =>
  cause instanceof Error && cause.message.length > 0 ? cause.message : '알 수 없는 오류';

interface UseImageUploadResult {
  /** 성공 시 S3 키(섹션 저장 API 에 전달), 실패 시 null. */
  readonly upload: (file: File, purpose: CatalogUploadPurpose) => Promise<string | null>;
  readonly isUploading: boolean;
  readonly error: string | null;
  /** 지난 업로드 오류를 지운다 — 같은 오류 줄을 쓰는 저장을 보낼 때 옛 업로드 오류가 남지 않게. */
  readonly clearError: () => void;
}

/**
 * presign → S3 직접 PUT 업로드. 크기 제한은 presigned PUT 이 서버에서 강제할 수
 * 없으므로(계약 주석 참조) 여기 클라이언트 검증이 1차 방어선이다.
 */
export const useImageUpload = (productId: string): UseImageUploadResult => {
  const presignMutation = trpc.storage.presignUpload.useMutation();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File, purpose: CatalogUploadPurpose): Promise<string | null> => {
    if (!(ALLOWED_IMAGE_CONTENT_TYPES as readonly string[]).includes(file.type)) {
      setError('jpeg · png · webp 형식만 업로드할 수 있습니다.');
      return null;
    }

    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      setError('이미지는 10MB 이하여야 합니다.');
      return null;
    }

    setIsUploading(true);
    setError(null);

    // 실패 단계를 구분해 문구에 남긴다 — presign(권한·검증)과 S3 PUT(CORS·버킷 정책·
    // 만료 URL)은 원인도 조치도 다른데, 같은 문구로 뭉개면 "다시 시도"만 반복하게 된다.
    let presigned: PresignUploadResult;

    try {
      presigned = await presignMutation.mutateAsync({
        productId,
        purpose,
        contentType: file.type as AllowedImageContentType,
      });
      if (!('key' in presigned)) throw new Error('일반 상품 이미지 업로드 응답이 아닙니다.');
    } catch (cause) {
      setError(`업로드 준비(presign)에 실패했습니다: ${describeCause(cause)}. 다시 시도해 주세요.`);
      setIsUploading(false);
      return null;
    }

    try {
      const response = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file,
      });

      if (!response.ok) {
        setError(`S3 업로드에 실패했습니다 (HTTP ${response.status}). 다시 시도해 주세요.`);
        return null;
      }

      return 'key' in presigned ? presigned.key : null;
    } catch (cause) {
      setError(
        `S3 업로드 요청이 실패했습니다: ${describeCause(cause)}. 네트워크 또는 버킷 CORS 설정을 확인해 주세요.`,
      );
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return { upload, isUploading, error, clearError: () => setError(null) };
};
